import { formatDistanceToNow, parseISO, isValid } from 'date-fns';
import {
    getCitationDisplayTitle,
    linkCitationChunk,
} from './citationDisplay';

export const INSIGHT_STATUS = {
    PENDING: 'pending',
    GENERATING: 'generating',
    COMPLETE: 'complete',
    FAILED: 'failed',
};

/** Consultant-facing insights always use accuracy-first retrieval. */
export const INSIGHTS_ACCURACY_MODE = true;

/** Category display order matching Figma nav. */
export const INSIGHT_CATEGORY_ORDER = [
    { key: 'reporting', label: 'Reporting' },
    { key: 'strategic_analysis', label: 'Strategic Analysis' },
    { key: 'validation', label: 'Validation' },
    { key: 'insights', label: 'Insights' },
];

export function groupInsightsByCategory(items = []) {
    const byCategory = new Map();
    for (const item of items) {
        const cat = item?.category || 'insights';
        if (!byCategory.has(cat)) byCategory.set(cat, []);
        byCategory.get(cat).push(item);
    }
    return INSIGHT_CATEGORY_ORDER.map(cat => ({
        ...cat,
        items: byCategory.get(cat.key) || [],
    })).filter(section => section.items.length > 0);
}

export function formatInsightGeneratedLabel(generatedAt) {
    if (!generatedAt) return 'Not yet generated';
    let date = generatedAt;
    if (typeof generatedAt === 'string') {
        date = parseISO(generatedAt);
    }
    if (!isValid(date)) return 'Not yet generated';
    return `Last generated ${formatDistanceToNow(date, { addSuffix: false })} ago`;
}

/** Remove numeric inline citation refs like [1] or [1]-[5] from insight body text. */
export function stripInlineCitationRefs(text = '') {
    if (typeof text !== 'string' || !text.trim()) return text;
    return text
        .replace(/\[(\d+)\]\s*-\s*\[(\d+)\]/g, '')
        .replace(/\[\d+\]/g, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/\s+([,.;:])/g, '$1')
        .replace(/,\s*\./g, '.')
        .replace(/,\s*,+/g, ',')
        .replace(/\(\s*\)/g, '')
        .trim();
}

export function buildInsightEvidenceItems(citations = [], evidence = [], chunks = [], ctx = {}) {
    const items = [];
    const seen = new Set();

    const pushItem = (key, title, ref, citation, linkedChunk) => {
        if (!title || seen.has(key)) return;
        seen.add(key);
        items.push({ key, title, ref, citation, linkedChunk });
    };

    for (let index = 0; index < (evidence || []).length; index += 1) {
        const row = evidence[index];
        const linked = chunks[index] || null;
        const citation = evidenceRowToCitation(row, linked);
        const title = getCitationDisplayTitle(citation, linked, ctx);
        const key = String(
            row?.source_id ||
                citation?.file_id ||
                citation?.irl_id ||
                citation?.observation_id ||
                title,
        );
        pushItem(key, title, row?.ref, citation, linked);
    }

    if (items.length) return items;

    for (const citation of citations || []) {
        const linked = linkCitationChunk(citation, chunks);
        const title = getCitationDisplayTitle(citation, linked, ctx);
        if (!title) continue;
        const key = String(citation?.ref ?? citation?.chunk_id ?? title);
        pushItem(key, title, citation?.ref, citation, linked);
    }

    return items;
}

function evidenceRowToCitation(row, linkedChunk) {
    const meta = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    return {
        source_type: row?.source_type || meta.source_type || linkedChunk?.source_type,
        file_id: meta.file_id ?? linkedChunk?.file_id,
        document_name: meta.metadata_storage_name || meta.document_name || linkedChunk?.document_name,
        metadata_storage_name: meta.metadata_storage_name || linkedChunk?.metadata_storage_name,
        irl_id: meta.irl_id ?? linkedChunk?.irl_id,
        irl_ref: meta.irl_ref || linkedChunk?.irl_ref,
        observation_id: meta.observation_id ?? linkedChunk?.observation_id,
        observation_ref: meta.observation_ref || linkedChunk?.observation_ref,
        workstream_repr: meta.workstream_repr || linkedChunk?.workstream_repr,
        tag_repr: meta.tag_repr || linkedChunk?.tag_repr,
        text_preview: row?.text_preview || linkedChunk?.chunk,
    };
}

export function resolveInsightEvidenceSummary(item) {
    return item?.evidence_summary || { documents: 0, irl_responses: 0, observations: 0 };
}

export function resolveInsightEvidenceCitedSummary(item) {
    const cited = item?.evidence_cited_summary;
    if (!cited) return null;
    if (cited.documents || cited.irl_responses || cited.observations) return cited;
    return null;
}

export function formatEvidenceSummaryLine(summary = {}) {
    const docs = Number(summary.documents) || 0;
    const irl = Number(summary.irl_responses) || 0;
    const obs = Number(summary.observations) || 0;
    const fileLabel = docs === 1 ? 'File' : 'Files';
    const irlLabel = irl === 1 ? 'IRL Response' : 'IRL Responses';
    const obsLabel = obs === 1 ? 'Observation' : 'Observations';
    return `Evidence: ${docs} ${fileLabel}, ${irl} ${irlLabel}, ${obs} ${obsLabel}`;
}
